import { MODALS } from "Containers/Pages/Modal";
import { useState, useRef, useMemo, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  findWordsearchWord,
  declareVictory,
} from "Store/features/public/publicPuzzleSlice";
import { setPublicModal } from "Store/features/public/publicStateSlice";
import Stopwatch from "../../Newsie/UploadContentNewsie/NewsiePreview/NewsieBottomBar/Stopwatch";

import PublicGameButtonGroup from "Components/PublicGameButtonGroup";
import { recordUserGameStat } from "API/Puzzles";

import "./PublicWordSearch.css";

// Carli is the prettiest girl in the whole universe
const PublicWordSearch = ({ wordsearchGrid: grid, words, postcastUser }) => {
  const dispatch = useDispatch();
  const [isDragging, setIsDragging] = useState(false);
  const [selectedCells, setSelectedCells] = useState([]);
  const [firstCell, setFirstCell] = useState(null);
  const [lastCell, setLastCell] = useState(null);
  const svgRef = useRef(null);
  const solutionSvgRef = useRef(null);
  const winner = useSelector((state) => state.publicPuzzleSlice.puzzle.winner);
  const puzzleId = useSelector((state) => state.publicPuzzleSlice.puzzleId);
  const authToken = useSelector(
    (state) => state.publicUserSlice.postcastAuthToken
  );
  const { time } = useSelector(
    (state) => state.publicPuzzleSlice.puzzle.timer
  );

  const victory = words?.every((w) => w.found);

  useEffect(() => {
    if (victory) {
      recordGame();
    }
  }, [victory]);

  const recordGame = useCallback(async () => {
    if (victory && !winner) {
      if (authToken) {
         recordUserGameStat( authToken, puzzleId, time, dispatch);
      }
      dispatch(declareVictory());
      dispatch(setPublicModal(MODALS.WINNER));
    }
  }, [victory, authToken, puzzleId, dispatch, winner, time]);

  const getCellCenter = (rowIndex, cellIndex) => {
    const cell = document.querySelector(
      `.wordsearch-grid-row:nth-child(${
        rowIndex + 1
      }) .wordsearch-grid-cell:nth-child(${cellIndex + 1})`
    );
    const rect = cell.getBoundingClientRect();
    return {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
    };
  };

  useEffect(() => {
    if (!solutionSvgRef.current) return;
    const solvedLinePoints = [];
    words.forEach((w) => {
      if (w.found) {
        solvedLinePoints.push(w.linePoints);
      }
    });

    console.log({ solvedLinePoints });
    const formattedSolvedLinePoints = solvedLinePoints.map((points) => {
      return points
        ?.map(({ rowIndex, cellIndex }) => {
          const { x, y } = getCellCenter(rowIndex, cellIndex);
          return `${x},${y}`;
        })
        .join(" ");
    });

    const refHtml = formattedSolvedLinePoints?.map(
      (points) => `
    <polyline 
      points="${points}" 
      style="fill:none;opacity:.5;stroke:green;stroke-width:20px;stroke-linecap:round;stroke-linejoin:round;"
    />
  `
    );

    solutionSvgRef.current.innerHTML = refHtml;
  }, [words]);

  const linePoints = useMemo(() => {
    const result = [];
    if (lastCell) {
      let lowCell, highCell, rise, run;
      lowCell = firstCell;
      highCell = lastCell;
      result.push(lowCell);
      rise = -(lastCell.rowIndex - firstCell.rowIndex);
      run = lastCell.cellIndex - firstCell.cellIndex;
      if (rise === 0 && run === 0) {
        result.push(lastCell);
      } else if (rise && run === 0) {
        const riseIsPositive = rise > 0;
        const riseCorrection = riseIsPositive ? -1 : 1;

        for (let i = 1; i <= Math.abs(rise); i += 1) {
          result.push({
            rowIndex: lowCell.rowIndex + i * riseCorrection,
            cellIndex: lowCell.cellIndex,
          });
        }
      } else if (run && rise === 0) {
        const runIsPositive = run > 0;
        const runCorrection = runIsPositive ? 1 : -1;

        for (let i = 1; i <= Math.abs(run); i += 1) {
          result.push({
            rowIndex: lowCell.rowIndex,
            cellIndex: lowCell.cellIndex + i * runCorrection,
          });
        }
      } else {
        const runIsPositive = run > 0;
        const riseIsPositive = rise > 0;
        const runCorrection = runIsPositive ? 1 : -1;
        const riseCorrection = riseIsPositive ? -1 : 1;

        for (let i = 1; i <= Math.abs(run); i += 1) {
          result.push({
            rowIndex: lowCell.rowIndex + riseCorrection * i,
            cellIndex: lowCell.cellIndex + runCorrection * i,
          });
        }
      }
    }
    return result;
  }, [firstCell, lastCell]);

  // useEffect(() => {
  //   console.log("Updated linePoints:", linePoints);
  // }, [linePoints]);

  useEffect(() => {
    // console.log("Updated lastCell:", lastCell);
    drawLine();
  }, [lastCell]);

  useEffect(() => {
    // console.log("Updated firstCell:", firstCell);
    drawLine();
  }, [firstCell]);
  const handleMouseDown = (rowIndex, cellIndex) => {
    setIsDragging(true);
    setSelectedCells([{ rowIndex, cellIndex }]);
    setFirstCell({ rowIndex, cellIndex });
    setLastCell({ rowIndex, cellIndex });
  };

  const handleMouseOver = (rowIndex, cellIndex) => {
    if (isDragging) {
      setSelectedCells([...selectedCells, { rowIndex, cellIndex }]);
      setLastCell({ rowIndex, cellIndex });
      // drawLine();
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    drawLine();

    // Handle the selected word
    let selectedWord;
    try {
      selectedWord = linePoints
        .map(({ rowIndex, cellIndex }) => grid[rowIndex][cellIndex])
        .join("");
    } catch (err) {
      selectedWord = "";
    }

    let shouldUpdate = false;
    const newWords = words.map((w) => {
      if (selectedWord === w.word) {
        shouldUpdate = true;
        return { ...w, linePoints, found: true };
      }
      return w;
    });
    if (shouldUpdate) dispatch(findWordsearchWord(newWords));
    setFirstCell(null);
    setLastCell(null);
    setSelectedCells([]);
    clearLine();
  };

  const handleTouchStart = (event, rowIndex, cellIndex) => {
    event.preventDefault();
    handleMouseDown(rowIndex, cellIndex);
  };

  const handleTouchMove = (event) => {
    event.preventDefault();
    const touch = event.touches[0];
    const element = document.elementFromPoint(touch.clientX, touch.clientY);
    if (element && element.classList.contains("wordsearch-grid-cell")) {
      const rowIndex = Array.from(
        element.parentElement.parentElement.children
      ).indexOf(element.parentElement);
      const cellIndex = Array.from(element.parentElement.children).indexOf(
        element
      );
      handleMouseOver(rowIndex, cellIndex);
    }
  };

  const handleTouchEnd = () => {
    setLastCell(null);
    setFirstCell(null);
    handleMouseUp();
  };

  const drawLine = useCallback(() => {
    if (winner) return;
    if (!svgRef.current) return;
    if (!linePoints) return;
    let points = [];
    try {
      points = linePoints
        ?.map(({ rowIndex, cellIndex }) => {
          const { x, y } = getCellCenter(rowIndex, cellIndex);
          return `${x},${y}`;
        })
        .join(" ");
    } catch (err) {
      console.log("Oops");
    }

    svgRef.current.innerHTML = `
      <polyline 
        points="${points}" 
        style="fill:none;stroke:red;opacity:.5;stroke-width:20px;stroke-linecap:round;stroke-linejoin:round;"
      />
    `;
  }, [winner, linePoints, svgRef]);

  const clearLine = () => {
    if (svgRef.current) {
      svgRef.current.innerHTML = "";
    }
  };

  const isSelected = (rowIndex, cellIndex) => {
    return selectedCells.some(
      (cell) => cell.rowIndex === rowIndex && cell.cellIndex === cellIndex
    );
  };

  const handleClickTrophyIcon = useCallback(() => {
    dispatch(setPublicModal(MODALS.WINNER));
  }, [dispatch]);
  const handleClickShareIcon = async () => {
    if (navigator.share) {
      navigator
        .share({
          title: "Newsie: News Puzzle",
          text: "Play this game!",
          // text: `I solved today\'s Newsie with a score of ${score}`+ "\n" + `in: ${minutes}:${seconds} with ${incorrectGuesses} incorrect guesses`,
          // url
        })
        .then(() => {
          console.log("Thanks for sharing!");
        })
        .catch(console.error);
    } else if (navigator.clipboard) {
      try {
        await navigator.clipboard.writeText("play this game!");
        console.log("Content copied to clipboard");
      } catch (err) {
        console.error("Failed to copy: ", err);
      }
    }
  };
  return (
    <div className="wordsearch-container">
      {words?.length ? (
        <div className="word-list-top-control-panel">
          <div className="crossword-clues-container-header">
            <b>Words | Time Elaped</b>
            <Stopwatch small={true} />
            <PublicGameButtonGroup />
          </div>
          <div className="word-list-container">
            {words?.map((w) => {
              return (
                <div className={`word-list-word ${w.found ? "found" : ""}`}>
                  {w.word}
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
      <div
        className="word-search"
        onMouseUp={handleMouseUp}
        onTouchEnd={handleTouchEnd}
        onTouchMove={handleTouchMove}
      >
        {grid.map((row, rowIndex) => (
          <div id={rowIndex} key={rowIndex} className="wordsearch-grid-row">
            {row.map((letter, cellIndex) => (
              <div
                key={cellIndex}
                id={cellIndex}
                className={`wordsearch-grid-cell ${
                  isSelected(rowIndex, cellIndex) ? "selected" : ""
                }`}
                onMouseDown={() => handleMouseDown(rowIndex, cellIndex)}
                onMouseOver={() => handleMouseOver(rowIndex, cellIndex)}
                onTouchStart={(e) => handleTouchStart(e, rowIndex, cellIndex)}
              >
                {letter}
              </div>
            ))}
          </div>
        ))}
      </div>
      <svg ref={svgRef} id="svg-overlay" />
      <svg ref={solutionSvgRef} id="svg-overlay" />
    </div>
  );
};

export default PublicWordSearch;
